import { CalendarOutlined, CheckOutlined, ClockCircleOutlined, EditOutlined } from "@ant-design/icons";
import { Avatar, Button, Flex, Input, InputRef, Typography, message } from "antd";
import dayjs from "dayjs";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import "pdfmake/build/vfs_fonts.js";
import React from "react";
import NoteTypeBadge from "../../components/Badge/NotesType.component";
import CategoryType from "../../components/Category/Category.component";
import GenderType from "../../components/Gender/GenderType.component";
import LocationType from "../../components/Location/LocationType.component";
import { NoteType } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { updateNote } from "../../service/notes.service";
import { setShowDemoSignupModal } from "../../slices/appStateSlice";
import { setNotesDetails } from "../../slices/notesSlice";
import { durationTimeFromType } from "../../utils/datamanipulation.utils";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import styles from "./notes.module.scss";

// @ts-ignore pdfmake types missing vfs property
(pdfMake as any).vfs = pdfFonts.vfs;

type TProps = {
  notesDetails: any;
};

export const CUSTOMISE_ICON = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.33333 1V3.66667M1 2.33333H3.66667M3 10.3333V13M1.66667 11.6667H4.33333M7.66667 1L9.19048 5.57143L13 7L9.19048 8.42857L7.66667 13L6.14286 8.42857L2.33333 7L6.14286 5.57143L7.66667 1Z"
      stroke="#6366F1"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NotesHeader = ({ notesDetails }: TProps) => {
  const dispatch = useAppDispatch();
  const componentName = "notes";
  const { sessionTerm } = useSpecialityReference();
  const untitledTitle = `Untitled ${sessionTerm}`;
  const titleRef = React.useRef<InputRef>(null);

  const { isDemoPath } = useAppSelector((state) => state.appState);
  const { isMobilePath } = useAppSelector((state) => state.appState);
  const [editTitle, setEditTitle] = React.useState(false);

  const updateTitle = async () => {
    setEditTitle(false);
    const value = titleRef.current?.input?.value;
    if (value && notesDetails && value !== notesDetails?.title) {
      const updateNoteData = {
        audio_id: notesDetails.id,
        title: value,
      };
      await updateNote(updateNoteData)
        .then(() => {
          message.success("Title updated successfully");
          dispatch(
            setNotesDetails({
              ...notesDetails,
              title: value,
            }),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const MidDot = () => {
    return <span>&middot;</span>;
  };

  return (
    <>
      {notesDetails && notesDetails.client_name && (
        <Flex
          justify="space-between"
          gap={10}
          style={{
            marginBottom: "10px",
          }}
          className={`${styles[`${componentName}__main-section-header-main`]}`}
        >
          <Flex gap={10} align="center" className={`${styles[`${componentName}__main-section-header-container`]}`}>
            <Avatar
              size={"small"}
              style={{
                backgroundColor: getRandomAvatarColor(notesDetails.client_name),
              }}
            >
              {notesDetails.client_name[0].toUpperCase()}
            </Avatar>
            <Typography>{notesDetails.client_name}</Typography>
            <GenderType gender_type={notesDetails?.gender_type} isBadge={false} />
          </Flex>
        </Flex>
      )}
      <Flex
        gap={10}
        justify="space-between"
        className={`${styles[`${componentName}__main-section-title-container`]}`}
        align={"flex-start"}
      >
        <span className={`${styles[`${componentName}__main-section-header`]}`}>
          {editTitle ? (
            <Input defaultValue={notesDetails?.title || untitledTitle} ref={titleRef} onBlur={updateTitle} />
          ) : (
            <span title={notesDetails?.title || untitledTitle}>{notesDetails?.title || untitledTitle}</span>
          )}
          &nbsp;
          {/* Imported notes are read-only, so NEITHER the rename pencil nor its
              confirm-tick should appear. Gating only the first arm of the ternary
              silently rendered the tick instead — a live save control on a note the
              backend refuses to update. */}
          {notesDetails?.note_type !== NoteType.EHR_PRIOR_NOTE &&
            (!editTitle ? (
              <Button
                shape="circle"
                icon={<EditOutlined />}
                className={`${styles[`${componentName}__main-section-header-edit`]}`}
                onClick={() => {
                  if (isDemoPath) {
                    dispatch(setShowDemoSignupModal(true));
                    return;
                  }
                  setEditTitle(true);
                }}
              />
            ) : (
              <Button
                shape="circle"
                type="primary"
                className={`${styles[`${componentName}__main-section-header-save`]}`}
                icon={<CheckOutlined />}
                onClick={updateTitle}
              />
            ))}
        </span>
      </Flex>
      <Flex className={`${styles[`${componentName}__main-detail`]}`} gap={5} align="center">
        <span>
          <CalendarOutlined /> &nbsp;
          {dayjs(notesDetails?.uploaded_at).format("MMM D, YYYY")} at{" "}
          {dayjs(notesDetails?.uploaded_at).format("h:mm A")}
        </span>

        {notesDetails.duration_type !== null && notesDetails.duration_type !== undefined && (
          <>
            <MidDot />
            <span>
              <ClockCircleOutlined />
              &nbsp;
              {durationTimeFromType(notesDetails.duration_type)}
            </span>
          </>
        )}
        <MidDot />
        <CategoryType category_type={notesDetails?.category_type} />
        {notesDetails?.location_type !== undefined && notesDetails?.location_type !== null && (
          <>
            <MidDot />
            <LocationType location_type={notesDetails?.location_type} />
          </>
        )}
        <MidDot />
        <span className={`${styles[`${componentName}__main-detail-note-type`]}`}>
          <NoteTypeBadge note_type={notesDetails?.note_type} note_type_info={notesDetails?.note_type_info} />
        </span>
      </Flex>
      <Flex className={`${styles[`${componentName}__main-detail-mobile`]}`} gap={10} vertical>
        <Flex gap={10}>
          <span>
            <CalendarOutlined /> &nbsp;
            {dayjs(notesDetails?.uploaded_at).format("MMM D, YYYY")} at{" "}
            {dayjs(notesDetails?.uploaded_at).format("h:mm A")}
          </span>

          {notesDetails?.duration_type !== undefined && notesDetails?.duration_type !== null && (
            <>
              <MidDot />
              <span>
                <ClockCircleOutlined />
                &nbsp;
                {durationTimeFromType(notesDetails.duration_type)}
              </span>
            </>
          )}
        </Flex>
        <Flex gap={10} wrap="wrap" className="mobile-badge-container">
          <div
            style={{
              flex:
                notesDetails?.location_type !== undefined && notesDetails?.location_type !== null
                  ? "1 1 calc(50% - 5px)"
                  : "1 1 100%",
            }}
            className="mobile-badge-wrapper"
          >
            <CategoryType category_type={notesDetails?.category_type} isBadge />
          </div>
          {notesDetails?.location_type !== undefined && notesDetails?.location_type !== null && (
            <div
              style={{
                flex: "1 1 calc(50% - 5px)",
              }}
              className="mobile-badge-wrapper"
            >
              <LocationType location_type={notesDetails?.location_type} isBadge />
            </div>
          )}
        </Flex>
        <Flex gap={10} wrap="wrap">
          <span className={`${styles[`${componentName}__main-detail-mobile-note-type`]}`}>
            <NoteTypeBadge internal note_type={notesDetails?.note_type} note_type_info={notesDetails?.note_type_info} />
          </span>
        </Flex>
      </Flex>
      {!notesDetails.is_owned && notesDetails?.owner_name && (
        <Flex style={{ marginTop: "10px", color: "var(--grey-text)" }}>
          Note created by: {notesDetails?.owner_name}
        </Flex>
      )}
    </>
  );
};

export default NotesHeader;
